import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FolderOpen, FileText, Clock, CheckCircle2, XCircle, Plus, Send,
  Download, Printer, User, GraduationCap, Building2, Calendar, Hash
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  draft: { color: 'bg-gray-100 text-gray-600', icon: Clock, label: 'Brouillon' },
  submitted: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Envoyee' },
  received: { color: 'bg-blue-100 text-blue-600', icon: Clock, label: 'Recue' },
  processing: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: 'En cours' },
  approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Validee' },
  completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Terminee' },
  rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejetee' },
};

export const MyDocumentsPage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [reqType, setReqType] = useState('');
  const [reqDesc, setReqDesc] = useState('');
  const [sending, setSending] = useState(false);
  const [card, setCard] = useState(null);
  const [showCard, setShowCard] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [reqRes, typesRes, catRes] = await Promise.all([
        axios.get(`${API}/documents/requests/me`),
        axios.get(`${API}/documents/request-types`),
        axios.get(`${API}/documents/categories`),
      ]);
      setRequests(reqRes.data);
      setRequestTypes(typesRes.data);
      setCategories(catRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadCard = async () => {
    try {
      const { data } = await axios.get(`${API}/students/me/card`);
      setCard(data);
      setShowCard(true);
    } catch (e) { toast.error('Erreur'); }
  };

  const submitRequest = async () => {
    if (!reqType) return;
    setSending(true);
    try {
      await axios.post(`${API}/documents/requests`, { type: reqType, description: reqDesc });
      toast.success('Demande soumise avec succes');
      setDlg(false); setReqType(''); setReqDesc('');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSending(false); }
  };

  const printCard = () => window.print();

  if (loading) return <div className="flex justify-center p-12"><div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  const pendingCount = requests.filter(r => ['submitted', 'received', 'processing'].includes(r.status)).length;
  const completedCount = requests.filter(r => ['approved', 'completed'].includes(r.status)).length;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="my-documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2"><FolderOpen className="w-5 h-5" /> Mes Dossiers</h1>
          <p className="text-sm text-gray-500">Documents et demandes administratives</p>
        </div>
        <div className="flex gap-2">
          {user?.role === 'student' && (
            <Button variant="outline" size="sm" onClick={loadCard} data-testid="student-card-btn">
              <Printer className="w-4 h-4 mr-1" /> Fiche Etudiant
            </Button>
          )}
          <Button size="sm" onClick={() => setDlg(true)} data-testid="new-request-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle Demande
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-blue-600">{requests.length}</p>
          <p className="text-xs text-gray-500">Total demandes</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
          <p className="text-xs text-gray-500">En attente</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-green-600">{completedCount}</p>
          <p className="text-xs text-gray-500">Completees</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="requests">
        <TabsList>
          <TabsTrigger value="requests" data-testid="tab-requests"><FileText className="w-4 h-4 mr-1" /> Demandes ({requests.length})</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories"><FolderOpen className="w-4 h-4 mr-1" /> Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="requests">
          <ScrollArea className="h-[400px]">
            {requests.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-gray-400 text-sm">
                Aucune demande. Cliquez "Nouvelle Demande" pour soumettre votre premiere demande.
              </CardContent></Card>
            ) : (
              <div className="space-y-2">
                {requests.map(r => {
                  const st = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
                  const StIcon = st.icon;
                  return (
                    <Card key={r.id} data-testid={`request-${r.id}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <StIcon className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="font-medium text-sm">{r.type_name}</p>
                              {r.description && <p className="text-xs text-gray-400 mt-0.5">{r.description}</p>}
                              <p className="text-[10px] text-gray-300 mt-0.5">{new Date(r.created_at).toLocaleDateString('fr-FR')} - Delai: {r.processing_days} jours</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={st.color}>{st.label}</Badge>
                            {r.notes && <p className="text-[10px] text-gray-400 mt-1">{r.notes}</p>}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        <TabsContent value="categories">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {categories.map(c => (
              <Card key={c.key}>
                <CardContent className="p-4 text-center">
                  <FolderOpen className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                  <p className="font-medium text-sm">{c.name_fr}</p>
                  <p className="text-xs text-gray-400 mt-1">{c.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* New Request Dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="new-request-dialog">
          <DialogHeader><DialogTitle>Nouvelle Demande Administrative</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type de demande *</Label>
              <Select value={reqType} onValueChange={setReqType}>
                <SelectTrigger><SelectValue placeholder="Choisir le type" /></SelectTrigger>
                <SelectContent>
                  {requestTypes.map(t => (
                    <SelectItem key={t.key} value={t.key}>
                      {t.name_fr} (delai: {t.processing_days}j)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description / Motif</Label>
              <textarea
                className="flex w-full rounded-md border bg-transparent px-3 py-2 text-sm min-h-[80px]"
                value={reqDesc}
                onChange={e => setReqDesc(e.target.value)}
                placeholder="Decrivez votre demande (optionnel)..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Annuler</Button>
            <Button onClick={submitRequest} disabled={!reqType || sending} data-testid="submit-request-btn">
              <Send className="w-4 h-4 mr-1" /> {sending ? 'Envoi...' : 'Soumettre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Student Card Dialog */}
      <Dialog open={showCard} onOpenChange={setShowCard}>
        <DialogContent className="max-w-lg" data-testid="student-card-dialog">
          <DialogHeader><DialogTitle>Fiche Etudiant</DialogTitle></DialogHeader>
          {card && (
            <div className="border-2 border-blue-600 rounded-lg p-4 space-y-3 print:border-black" id="student-card" data-testid="student-card">
              <div className="text-center border-b pb-3">
                <p className="text-xs text-gray-400 uppercase">Republique Democratique du Congo</p>
                <p className="text-lg font-bold text-blue-700">{card.university}</p>
                <p className="text-xs text-gray-500">CARTE D'ETUDIANT</p>
              </div>
              <div className="flex gap-4">
                <div className="w-20 h-24 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                  <User className="w-10 h-10 text-gray-400" />
                </div>
                <div className="space-y-1 text-sm flex-1">
                  <div className="flex gap-2"><span className="text-gray-500 w-20">Nom:</span><strong>{card.last_name}</strong></div>
                  <div className="flex gap-2"><span className="text-gray-500 w-20">Prenom:</span><strong>{card.first_name}</strong></div>
                  <div className="flex gap-2"><span className="text-gray-500 w-20">Matricule:</span><strong className="text-blue-600">{card.matricule}</strong></div>
                  {card.birth_date && <div className="flex gap-2"><span className="text-gray-500 w-20">Ne(e) le:</span><span>{card.birth_date}</span></div>}
                  {card.birth_place && <div className="flex gap-2"><span className="text-gray-500 w-20">A:</span><span>{card.birth_place}</span></div>}
                  {card.gender && <div className="flex gap-2"><span className="text-gray-500 w-20">Genre:</span><span>{card.gender === 'M' ? 'Masculin' : 'Feminin'}</span></div>}
                  {card.nationality && <div className="flex gap-2"><span className="text-gray-500 w-20">Nationalite:</span><span>{card.nationality}</span></div>}
                </div>
              </div>
              <div className="border-t pt-2 space-y-1 text-xs">
                {card.faculty && <div className="flex gap-2"><Building2 className="w-3 h-3 text-gray-400 mt-0.5" /><span>{card.faculty}</span></div>}
                {card.department && <div className="flex gap-2"><GraduationCap className="w-3 h-3 text-gray-400 mt-0.5" /><span>{card.department}</span></div>}
                {card.program && <div className="flex gap-2"><Hash className="w-3 h-3 text-gray-400 mt-0.5" /><span>{card.program}</span></div>}
                {card.academic_year && <div className="flex gap-2"><Calendar className="w-3 h-3 text-gray-400 mt-0.5" /><span>Annee: {card.academic_year}</span></div>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCard(false)}>Fermer</Button>
            <Button onClick={printCard} data-testid="print-card-btn"><Printer className="w-4 h-4 mr-1" /> Imprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
